import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  FileText, Upload, ArrowLeft, ArrowRight, Check, Loader2,
  AlertCircle, CheckCircle2,
} from 'lucide-react';
import { toastMessage } from '@/utils/toast-message';
import { erpPath } from '@/utils/erp-host';
import {
  bulkImportApi, type ImportModule, type ImportField,
  type ImportRowError, type CommitResult,
} from './bulk-import.api';

const MODULE_INFO: Record<ImportModule, { label: string; desc: string; listPath: string }> = {
  students: { label: 'Students', desc: 'Bring in your student roster, keyed by your own Student ID.', listPath: 'students' },
  fees: { label: 'Fees', desc: 'Set up fee installments (and anything already collected) per student.', listPath: 'fees' },
};

const STEP_TITLES = ['Select Data', 'Upload File', 'Map Columns', 'Review', 'Import'];

// Mirrors services/dashboard/utils/csv-import.utils.js MAX_IMPORT_ROWS — kept
// in sync manually since the frontend can't import a backend file. Checking
// client-side too means an oversized file is rejected right after upload
// instead of only after the user has already mapped columns and clicked
// through to commit.
const MAX_IMPORT_ROWS = 5000;

// Wraps a CSV cell that starts with a formula-trigger character in a
// leading apostrophe so Excel/Sheets renders it as text instead of
// evaluating it — this file is built from the user's own uploaded data,
// which may get forwarded to someone else once exported as an error report.
const csvSafeCell = (value: string): string =>
  /^[=+\-@]/.test(value) ? `'${value}` : value;

const BulkImport: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselected = searchParams.get('module');
  const initialModule: ImportModule | null = preselected === 'students' || preselected === 'fees' ? preselected : null;
  const [step, setStep] = useState(initialModule ? 1 : 0);
  const [module, setModule] = useState<ImportModule | null>(initialModule);
  const [fields, setFields] = useState<ImportField[]>([]);

  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<Record<string, string>[]>([]);

  const [mapping, setMapping] = useState<Record<string, string>>({});

  const [validating, setValidating] = useState(false);
  const [validCount, setValidCount] = useState(0);
  const [rowErrors, setRowErrors] = useState<ImportRowError[]>([]);

  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState<CommitResult | null>(null);

  useEffect(() => {
    if (!module) return;
    bulkImportApi.getTemplate(module).then((res) => {
      if (res.success && res.data) setFields(res.data.fields);
    });
  }, [module]);

  const reset = () => {
    setStep(0); setModule(null); setFields([]);
    setFile(null); setHeaders([]); setRows([]);
    setMapping({}); setValidCount(0); setRowErrors([]); setResult(null);
  };

  const handleSelectModule = (m: ImportModule) => {
    // Selecting a module always jumps straight to step 1, so if the user hit
    // Back from an earlier attempt and picked a *different* module here, any
    // file/mapping/validation state from that earlier attempt must be
    // cleared — otherwise step 1 shows the old file as already "uploaded"
    // for the newly chosen module, and its stale headers/rows carry through
    // to column mapping and validation for the wrong dataset.
    setModule(m);
    setFields([]);
    setFile(null); setHeaders([]); setRows([]);
    setMapping({}); setValidCount(0); setRowErrors([]); setResult(null);
    setStep(1);
  };

  const handleFile = async (selected: File) => {
    if (!selected.name.endsWith('.csv')) {
      toastMessage('Please upload a CSV file', 'error');
      return;
    }
    if (selected.size > 10 * 1024 * 1024) {
      toastMessage('File size must be under 10MB', 'error');
      return;
    }
    setFile(selected);
    setUploading(true);
    try {
      const res = await bulkImportApi.uploadCsv(module!, selected);
      if (res.success && res.data) {
        if (res.data.totalRows > MAX_IMPORT_ROWS) {
          toastMessage(`This file has ${res.data.totalRows} rows — please split it into files of ${MAX_IMPORT_ROWS} rows or fewer.`, 'error');
          setFile(null);
          return;
        }
        setHeaders(res.data.headers);
        setRows(res.data.rows);
        toastMessage(`Parsed ${res.data.totalRows} rows`, 'success');
      } else {
        toastMessage(res.message || 'Failed to parse CSV', 'error');
        setFile(null);
      }
    } finally {
      setUploading(false);
    }
  };

  // Reactive on [headers, fields] rather than done inline in handleFile —
  // the template fetch (GET .../template, kicked off when `module` is set)
  // and the CSV upload/parse race independently, so whichever finishes last
  // must still trigger the auto-map. Only fills in fields not already
  // mapped, so it never clobbers a manual choice.
  useEffect(() => {
    if (headers.length === 0 || fields.length === 0) return;
    setMapping((prev) => {
      const next = { ...prev };
      fields.forEach((f) => {
        if (next[f.key]) return;
        const match = headers.find(
          (h) => h.toLowerCase() === f.key.toLowerCase() || h.toLowerCase() === f.label.toLowerCase()
        );
        if (match) next[f.key] = match;
      });
      return next;
    });
  }, [headers, fields]);

  const requiredFieldsMapped = fields.filter((f) => f.required).every((f) => mapping[f.key]);

  // Two different fields mapped to the same source column silently loses
  // whichever field is applied second (or produces nonsense in both) — the
  // <select> per field has no visibility into what else is already chosen,
  // so this has to be checked across the whole mapping instead.
  const mappedHeaderCounts = Object.values(mapping).reduce<Record<string, number>>((acc, h) => {
    if (h) acc[h] = (acc[h] || 0) + 1;
    return acc;
  }, {});
  const duplicateMappedHeaders = Object.keys(mappedHeaderCounts).filter((h) => mappedHeaderCounts[h] > 1);
  const mappingIsValid = requiredFieldsMapped && duplicateMappedHeaders.length === 0;

  const handleValidate = async () => {
    setValidating(true);
    try {
      const res = await bulkImportApi.validateRows(module!, rows, mapping);
      if (res.success && res.data) {
        setValidCount(res.data.validCount);
        setRowErrors(res.data.errors);
        setStep(3);
      } else {
        toastMessage(res.message || 'Validation failed', 'error');
      }
    } finally {
      setValidating(false);
    }
  };

  const handleCommit = async () => {
    setCommitting(true);
    setStep(4);
    try {
      const res = await bulkImportApi.commitRows(module!, rows, mapping, file?.name || 'import.csv');
      if (res.success && res.data) {
        setResult(res.data);
        toastMessage(`Import complete: ${res.data.successCount} of ${res.data.totalRows} rows imported`, 'success');
      } else {
        toastMessage(res.message || 'Import failed', 'error');
        setStep(3);
      }
    } finally {
      setCommitting(false);
    }
  };

  const handleDownloadTemplate = () => {
    if (!module) return;
    const csv = [fields.map((f) => csvSafeCell(f.label)).join(',')].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${module}-import-template.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDownloadErrors = (errors: ImportRowError[]) => {
    const cols = Array.from(new Set(errors.flatMap((e) => Object.keys(e.raw_row))));
    const csvCell = (value: string) => `"${csvSafeCell(value).replace(/"/g, '""')}"`;
    const csv = [
      ['Row', ...cols, 'Error'].join(','),
      ...errors.map((e) => [e.row_number, ...cols.map((c) => csvCell(e.raw_row[c] || '')), csvCell(e.error_message)].join(',')),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${module}-import-errors.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="h-full flex flex-col bg-gray-50 dark:bg-gray-950">
      <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-10">
        <div className="p-4">
          <div className="flex items-center space-x-3">
            <div className="bg-brandColor p-2 rounded-lg">
              <FileText className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Bulk Import</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">Import Students and Fees from a CSV file</p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="p-6 max-w-5xl mx-auto w-full space-y-6">

          {/* Stepper */}
          <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between">
              {STEP_TITLES.map((title, i) => (
                <React.Fragment key={title}>
                  <div className="flex flex-col items-center flex-1">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold ${
                      step === i ? 'bg-brandColor text-white ring-4 ring-cyan-200 dark:ring-cyan-800'
                      : step > i ? 'bg-green-500 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-500'
                    }`}>
                      {step > i ? <Check className="h-4 w-4" /> : i + 1}
                    </div>
                    <p className={`mt-1 text-xs font-medium hidden sm:block ${step === i ? 'text-brandColor' : 'text-gray-500 dark:text-gray-400'}`}>{title}</p>
                  </div>
                  {i < STEP_TITLES.length - 1 && (
                    <div className={`h-0.5 flex-1 mx-1 ${step > i ? 'bg-green-500' : 'bg-gray-200 dark:bg-gray-700'}`} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Step content */}
          <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-8">

            {step === 0 && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">What do you want to import?</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {(Object.keys(MODULE_INFO) as ImportModule[]).map((m) => (
                    <button
                      key={m}
                      onClick={() => handleSelectModule(m)}
                      className="text-left p-6 border-2 border-gray-200 dark:border-gray-700 rounded-lg hover:border-brandColor transition-colors"
                    >
                      <p className="font-semibold text-gray-900 dark:text-white">{MODULE_INFO[m].label}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{MODULE_INFO[m].desc}</p>
                    </button>
                  ))}
                </div>
                {/*
                  This must NOT be conditioned on `module === 'fees'` — selecting
                  a module advances `step` to 1 in the same handler, so a
                  step-0-only render would never actually happen and this
                  warning would silently never show.
                */}
                <p className="mt-4 text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 p-3 rounded-lg">
                  Importing Fees? Import Students first — Fees rows are matched to students by the Student ID you assign during that import.
                </p>
              </div>
            )}

            {step === 1 && module && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Upload {MODULE_INFO[module].label} CSV</h3>
                  <button
                    onClick={handleDownloadTemplate}
                    disabled={fields.length === 0}
                    className="text-sm text-brandColor hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Download CSV template
                  </button>
                </div>
                <div
                  onDragEnter={(e) => { e.preventDefault(); setIsDragging(true); }}
                  onDragOver={(e) => e.preventDefault()}
                  onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
                  onDrop={(e) => { e.preventDefault(); setIsDragging(false); const f = e.dataTransfer.files[0]; if (f) handleFile(f); }}
                  onClick={() => document.getElementById('bulk-import-file-input')?.click()}
                  className={`relative border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all ${
                    isDragging ? 'border-brandColor bg-cyan-50 dark:bg-cyan-900/20' : 'border-gray-300 dark:border-gray-700 hover:border-cyan-400'
                  } ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
                >
                  <input
                    id="bulk-import-file-input" type="file" accept=".csv" className="hidden"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ''; }}
                  />
                  {uploading ? (
                    <div className="flex flex-col items-center space-y-3">
                      <Loader2 className="h-10 w-10 text-brandColor animate-spin" />
                      <p className="text-sm text-gray-600 dark:text-gray-400">Uploading and parsing...</p>
                    </div>
                  ) : file ? (
                    <div className="flex items-center justify-center space-x-3">
                      <CheckCircle2 className="h-8 w-8 text-green-500" />
                      <div className="text-left">
                        <p className="font-medium text-gray-900 dark:text-white">{file.name}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{rows.length} rows parsed</p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center space-y-3">
                      <Upload className="h-10 w-10 text-gray-400" />
                      <p className="text-gray-700 dark:text-gray-300">Drop your CSV here, or click to browse</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Max 10MB — first row must be column headers</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {step === 2 && module && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Map your columns</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Match each column from your file to the matching field below. Fields marked * are required.
                </p>
                <div className="space-y-3">
                  {fields.map((f) => (
                    <div key={f.key} className="grid grid-cols-2 gap-4 items-center">
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{f.label}{f.required && <span className="text-red-500"> *</span>}</p>
                        {f.help && <p className="text-xs text-gray-500 dark:text-gray-400">{f.help}</p>}
                      </div>
                      <select
                        value={mapping[f.key] || ''}
                        onChange={(e) => setMapping((m) => ({ ...m, [f.key]: e.target.value }))}
                        className="border border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:text-white rounded-lg px-3 py-2 text-sm"
                      >
                        <option value="">— Not mapped —</option>
                        {headers.map((h) => <option key={h} value={h}>{h}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
                {!requiredFieldsMapped && (
                  <p className="text-sm text-amber-700 dark:text-amber-400">Map every required field to continue.</p>
                )}
                {duplicateMappedHeaders.length > 0 && (
                  <p className="text-sm text-red-700 dark:text-red-400">
                    Column{duplicateMappedHeaders.length > 1 ? 's' : ''} {duplicateMappedHeaders.map((h) => `"${h}"`).join(', ')} {duplicateMappedHeaders.length > 1 ? 'are' : 'is'} mapped to more than one field — each column can only be mapped once.
                  </p>
                )}
              </div>
            )}

            {step === 3 && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Review before importing</h3>
                <div className="flex gap-4">
                  <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg flex-1">
                    <p className="text-2xl font-bold text-green-700 dark:text-green-400">{validCount}</p>
                    <p className="text-sm text-green-700 dark:text-green-400">rows ready to import</p>
                  </div>
                  <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg flex-1">
                    <p className="text-2xl font-bold text-red-700 dark:text-red-400">{rowErrors.length}</p>
                    <p className="text-sm text-red-700 dark:text-red-400">rows will be skipped</p>
                  </div>
                </div>
                {rowErrors.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">Skipped rows — fix these in your file and re-upload if you want them included</p>
                      <button onClick={() => handleDownloadErrors(rowErrors)} className="text-sm text-brandColor hover:underline">Download error report</button>
                    </div>
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-auto max-h-80">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
                          <tr>
                            <th className="text-left px-3 py-2 font-medium text-gray-700 dark:text-gray-300">Row</th>
                            <th className="text-left px-3 py-2 font-medium text-gray-700 dark:text-gray-300">Problem</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rowErrors.map((e) => (
                            <tr key={e.row_number} className="border-t border-gray-100 dark:border-gray-800">
                              <td className="px-3 py-2 align-top text-gray-500 dark:text-gray-400">{e.row_number}</td>
                              <td className="px-3 py-2 align-top text-red-700 dark:text-red-400 flex items-start gap-2">
                                <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                                <span>{e.error_message}</span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                {validCount === 0 && (
                  <p className="text-sm text-red-700 dark:text-red-400">No rows are ready to import — fix the errors above and re-upload.</p>
                )}
              </div>
            )}

            {step === 4 && (
              <div className="text-center py-8">
                {committing ? (
                  <div className="flex flex-col items-center space-y-4">
                    <Loader2 className="h-12 w-12 text-brandColor animate-spin" />
                    <p className="text-gray-700 dark:text-gray-300">Importing your data...</p>
                  </div>
                ) : result ? (
                  <div className="flex flex-col items-center space-y-4">
                    <CheckCircle2 className="h-14 w-14 text-green-500" />
                    <div>
                      <p className="text-xl font-semibold text-gray-900 dark:text-white">Import complete</p>
                      <p className="text-gray-600 dark:text-gray-400 mt-1">
                        {result.successCount} of {result.totalRows} rows imported
                        {result.errorCount > 0 && `, ${result.errorCount} skipped`}.
                      </p>
                      {(result.createdCount != null || result.updatedCount != null) && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                          {result.createdCount ?? 0} new, {result.updatedCount ?? 0} existing record{result.updatedCount === 1 ? '' : 's'} updated.
                        </p>
                      )}
                      {(result.paymentsCreatedCount != null && result.paymentsCreatedCount > 0) && (
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {result.paymentsCreatedCount} payment{result.paymentsCreatedCount === 1 ? '' : 's'} recorded.
                        </p>
                      )}
                    </div>
                    {result.warnings && result.warnings.length > 0 && (
                      <div className="w-full text-left">
                        <p className="text-sm font-medium text-amber-700 dark:text-amber-400 mb-2">
                          {result.warnings.length} row{result.warnings.length === 1 ? '' : 's'} needed a closer look — nothing was overwritten:
                        </p>
                        <div className="border border-amber-200 dark:border-amber-900 rounded-lg overflow-auto max-h-56 bg-amber-50 dark:bg-amber-900/20">
                          <ul className="divide-y divide-amber-200 dark:divide-amber-900">
                            {result.warnings.map((w) => (
                              <li key={w.row_number} className="px-3 py-2 text-sm text-amber-800 dark:text-amber-300 flex items-start gap-2">
                                <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                                <span><strong>Row {w.row_number}:</strong> {w.message}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}
                    <div className="flex gap-3">
                      {result.errorCount > 0 && (
                        <button onClick={() => handleDownloadErrors(result.errors)} className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300">
                          Download error report
                        </button>
                      )}
                      <button
                        onClick={() => navigate(erpPath(MODULE_INFO[module!].listPath))}
                        className="px-4 py-2 rounded-lg bg-brandColor text-white"
                      >
                        View {MODULE_INFO[module!].label}
                      </button>
                      <button onClick={reset} className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300">
                        Import more
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
            )}
          </div>

          {/* Nav buttons */}
          {step < 4 && (
            <div className="flex justify-between">
              <button
                onClick={() => (step === 0 ? undefined : setStep(step - 1))}
                disabled={step === 0}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium ${
                  step === 0 ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 cursor-not-allowed' : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200'
                }`}
              >
                <ArrowLeft className="h-4 w-4" /> Back
              </button>

              {step === 1 && (
                <button
                  onClick={() => setStep(2)}
                  disabled={!file || rows.length === 0 || uploading}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium ${
                    file && rows.length > 0 && !uploading ? 'bg-brandColor text-white' : 'bg-gray-300 dark:bg-gray-700 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  Next <ArrowRight className="h-4 w-4" />
                </button>
              )}
              {step === 2 && (
                <button
                  onClick={handleValidate}
                  disabled={!mappingIsValid || validating}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium ${
                    mappingIsValid && !validating ? 'bg-brandColor text-white' : 'bg-gray-300 dark:bg-gray-700 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {validating ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Check for errors <ArrowRight className="h-4 w-4" />
                </button>
              )}
              {step === 3 && (
                <button
                  onClick={handleCommit}
                  disabled={validCount === 0}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium ${
                    validCount > 0 ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-gray-300 dark:bg-gray-700 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  Import {validCount} row{validCount === 1 ? '' : 's'}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkImport;
